<template>
  <div class="creator-wrapper">
    <div class="creator-card">
      <button class="help-fab" type="button" @click="showHelpAlert">Help</button>
      <h1 class="creator-title">
        <template v-if="phase === 'name' || !gameName">{{ mode === 'edit' ? 'Game Config Editor' : 'New game on Warhall' }}</template>
        <template v-else-if="phase === 'firstdata'">{{ gameName }} — My First Data</template>
        <template v-else>{{ gameName }} {{ wizardMode === 'advanced' ? 'Advanced Configuration' : 'Basic Configuration' }}</template>
      </h1>

      <!-- ─────────────── PHASE: name ─────────────── -->
      <div v-if="phase === 'name'" class="phase-name">
        <p class="phase-subtitle">Let's start by naming your game system.</p>
        <div class="field-row">
          <label class="field-label">Game Name <span class="required">*</span></label>
          <input
            v-model="gameName"
            type="text"
            class="field-input field-input-lg"
            placeholder="e.g. MyGameSystem"
            @keyup.enter="startWizard"
            autofocus
          />
          <p v-if="gameNameError" class="field-error">{{ gameNameError }}</p>
        </div>

        <div class="nav-buttons">
          <span></span>
          <button class="btn btn-primary" @click="startWizard">Next →</button>
        </div>
      </div>

      <!-- ─────────────── PHASE: config ─────────────── -->
      <div v-else-if="phase === 'config'">
        <!-- Named Step Stepper -->
        <div class="step-stepper">
          <div
            v-for="(step, i) in displaySteps"
            :key="i"
            class="step-stepper-item"
            :class="{ 'step-active': (i + displayStepOffset) === configStep, 'step-done': (i + displayStepOffset) < configStep }"
            @click="(i + displayStepOffset) < configStep && $router.push({ query: { phase: 'config', step: step.title.toLowerCase().replace(/\s+/g, '-') } })"
          >
            <div class="step-stepper-dot">{{ i + 1 }}</div>
            <div class="step-stepper-label">{{ step.title }}</div>
          </div>
        </div>

        <!-- Fields -->
        <div class="step-content">

          <!-- ── Formations sub-wizard (step index 1 only) ── -->
          <div v-if="configStep === 1" class="formation-wizard">
            <p class="formation-substep-label">{{ formationSubStepLabel }}</p>

            <!-- Sub-step 0: which formations exist? -->
            <div v-if="formationSubStep === 0">
              <p class="field-desc mb-sm">Select all formation types that exist in your game system.</p>
              <div class="buttons-grid">
                <label
                  v-for="f in formationOptions"
                  :key="f.name"
                  class="btn-option"
                  :class="{ 'btn-option--checked': selectedFormations.includes(f.name) }"
                >
                  <input
                    type="checkbox"
                    :checked="selectedFormations.includes(f.name)"
                    @change="toggleFormation(f.name, $event.target.checked)"
                    class="btn-option-check"
                  />
                  <div class="btn-option-icon">
                    <img v-if="f.icon" :src="f.icon" :alt="f.name" />
                    <div v-else class="btn-option-icon-placeholder"></div>
                  </div>
                  <div class="btn-option-text">
                    <span class="btn-option-name">{{ f.label }}</span>
                    <span class="btn-option-desc">{{ f.description }}</span>
                  </div>
                </label>
              </div>
              <p v-if="formationError" class="field-error mt-sm">{{ formationError }}</p>
            </div>

            <!-- Sub-step 1: which is the default? -->
            <div v-else-if="formationSubStep === 1">
              <p class="field-desc mb-sm">This formation is assigned to units that have no specific formation rule.</p>
              <select v-model="config.DefaultFormation" class="field-input" style="max-width:320px">
                <option v-for="f in selectedFormations" :key="f" :value="f">{{ f }}</option>
              </select>
            </div>

            <!-- Sub-step 2: conditional parameters -->
            <div v-else-if="formationSubStep === 2">
              <!-- RankAndFile → pivot measurement -->
              <template v-if="config.DefaultFormation === 'RankAndFile'">
                <div class="field-row" style="max-width:420px">
                  <label class="field-label">Pivot Distance Measurement</label>
                  <p class="field-desc">How rotation pivot distance is measured on the board.</p>
                  <select v-model="config.PivotDistanceMeasurement" class="field-input">
                    <option value="Straight">Straight (chord distance)</option>
                    <option value="Arc">Arc (arc length)</option>
                  </select>
                </div>
              </template>
              <!-- Cloud → separation + selection mode -->
              <template v-else-if="config.DefaultFormation === 'Cloud'">
                <div class="field-row" style="max-width:420px">
                  <label class="field-label">Max Cloud Separation (inches)</label>
                  <p class="field-desc">Maximum gap between soldiers before a cohesion violation is shown.</p>
                  <input v-model.number="config.MaxCloudSeparation" type="number" step="0.1" class="field-input field-input-narrow" />
                </div>
                <div class="field-row" style="max-width:420px; margin-top:1.2rem">
                  <label class="field-label">Individual Soldier Selection</label>
                  <p class="field-desc">Off = select the whole unit, On = select individual soldiers.</p>
                  <label class="toggle-label">
                    <div class="toggle" :class="{ 'toggle-on': config.DefaultSoldierSelectionMode }" @click="config.DefaultSoldierSelectionMode = !config.DefaultSoldierSelectionMode">
                      <div class="toggle-thumb"></div>
                    </div>
                    <span class="toggle-text">{{ config.DefaultSoldierSelectionMode ? 'Yes' : 'No' }}</span>
                  </label>
                </div>
              </template>
            </div>

          </div>

          <!-- ── Tokens step (step index 2) ── -->
          <div v-else-if="configStep === 2" class="tokens-step">
            <h3 style="margin-top:0;color:#f1f5f9">🟡 Tokens</h3>
            <p class="field-desc">In this section you can add any kind of tokens that your game might need.</p>
            <TokensEditor
              :value="tokensJson"
              :skin-size="64"
              @input="tokensJson = $event"
              @catalogue-loaded="tokenCatalogue = $event"
            />
          </div>

          <!-- ── Terrain step (step index 3) ── -->
          <div v-else-if="configStep === 3" class="tokens-step">
            <h3 style="margin-top:0;color:#f1f5f9">🏔️ Terrain</h3>
            <p class="field-desc">In this section you can add terrain elements to your game.</p>
            <TerrainEditor
              :value="terrainsJson"
              :skin-size="64"
              @input="terrainsJson = $event"
            />
          </div>

          <!-- ── Deployments step (step index 4) ── -->
          <div v-else-if="configStep === 4" class="deployments-step">
            <h3 style="margin-top:0;color:#f1f5f9">🟩 Deployments</h3>
            <p class="field-desc">In this section you can add deployment lines your game might need.</p>
            <DeploymentsEditor
              :value="deploymentsJson"
              @input="deploymentsJson = $event"
            />
          </div>

          <!-- ── Objectives step (step index 5) ── -->
          <div v-else-if="configStep === 5" class="objectives-step">
            <h3 style="margin-top:0;color:#f1f5f9">🎯 Objectives</h3>
            <p class="field-desc">If your game uses objectives, here you can configure where they are located on the battlefield and what they are called.</p>
            <ObjectivesEditor
              :value="objectivesJson"
              :available-token-skins="selectedTokenSkins"
              :battlefield-width="config.BattlegroundWidth"
              :battlefield-height="config.BattlegroundHeight"
              @input="objectivesJson = $event"
            />
          </div>

          <!-- ── My First Data (step index 6, create mode only) ── -->
          <div v-else-if="configStep === 6 && mode === 'create'" class="my-first-data-step">
            <MyFirstDataEditor
              :initial-phase="firstDataState === 'wizard-done' ? 'done' : 'question'"
              :factions="firstData.factions"
              :sections="firstData.sections"
              :first-unit-name="firstData.firstUnitName"
              :first-unit-faction="firstData.firstUnitFaction"
              :first-unit-section="firstData.firstUnitSection"
              :profile-names="firstData.profileNames"
              :picked-skin="firstDataPickedSkin"
              @answer-no="onFirstDataAnswerNo"
              @wizard-start="onFirstDataWizardStart"
            />
          </div>

          <!-- Generic fields grid (all other steps) -->
          <div v-else class="fields-grid" :class="{ 'mb-sm': configStep === 7 }">
            <p v-if="currentConfigStep.intro" class="step-intro field-desc full-width">{{ currentConfigStep.intro }}</p>
            <div
              v-for="field in currentConfigStep.fields"
              :key="field.key"
              class="field-row"
              :class="{ 'full-width': field.type === 'string' || field.type === 'checklist' }"
            >
              <label class="field-label">{{ field.label }}</label>
              <p class="field-desc">{{ field.description }}</p>

              <!-- Bool toggle -->
              <label v-if="field.type === 'bool'" class="toggle-label">
                <div class="toggle" :class="{ 'toggle-on': config[field.key] }" @click="config[field.key] = !config[field.key]">
                  <div class="toggle-thumb"></div>
                </div>
                <span class="toggle-text">{{ config[field.key] ? 'Yes' : 'No' }}</span>
              </label>

              <!-- Select dropdown -->
              <select v-else-if="field.type === 'select'" v-model="config[field.key]" class="field-input">
                <option v-for="opt in field.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>

              <!-- Number (integer) -->
              <input
                v-else-if="field.type === 'number'"
                v-model.number="config[field.key]"
                type="number"
                step="1"
                class="field-input field-input-narrow"
              />

              <!-- Float -->
              <input
                v-else-if="field.type === 'float'"
                v-model.number="config[field.key]"
                type="number"
                step="0.1"
                class="field-input field-input-narrow"
              />

              <!-- Checklist (multi-select checkboxes) -->
              <div v-else-if="field.type === 'checklist'" class="checklist-group">
                <label v-for="opt in field.options" :key="opt" class="checklist-item">
                  <input
                    type="checkbox"
                    :checked="config[field.key].includes(opt)"
                    @change="toggleChecklist(field.key, opt, $event.target.checked)"
                  />
                  <img v-if="field.icons && field.icons[opt]" :src="field.icons[opt]" :alt="opt" class="checklist-icon" />
                  <span>{{ opt }}</span>
                </label>
              </div>

              <!-- Color picker (hex output) -->
              <div v-else-if="field.type === 'colorpicker-hex'" class="colorpicker-row">
                <input
                  type="color"
                  :value="config[field.key]"
                  class="colorpicker-swatch"
                  @input="config[field.key] = $event.target.value"
                />
              </div>

              <!-- Color picker (RGB output) -->
              <div v-else-if="field.type === 'colorpicker-rgb'" class="colorpicker-row">
                <input
                  type="color"
                  :value="rgbToHex(config[field.key])"
                  class="colorpicker-swatch"
                  @input="config[field.key] = hexToRgb($event.target.value)"
                />
              </div>

              <!-- String -->
              <input
                v-else
                :value="config[field.key]"
                @input="config[field.key] = field.alphanumeric ? $event.target.value.replace(/[^a-zA-Z0-9]/g, '') : $event.target.value"
                type="text"
                class="field-input"
                :placeholder="field.placeholder || ''"
                :required="field.alphanumeric || false"
              />
            </div>
          </div>

          <!-- Buttons step (step index 10) -->
          <div v-if="configStep === 10" class="buttons-step">
            <p class="field-desc">Select which InfoPanel buttons are visible in your game.</p>
            <div v-for="group in buttonGroups" :key="group.label" class="btn-group">
              <h4 class="btn-group-label">{{ group.label }}</h4>
              <div class="buttons-grid">
                <label
                  v-for="btn in group.buttons"
                  :key="btn.name"
                  class="btn-option"
                  :class="{ 'btn-option--checked': shownButtons.includes(btn.name) }"
                >
                  <input
                    type="checkbox"
                    :checked="shownButtons.includes(btn.name)"
                    @change="toggleShownButton(btn.name, $event.target.checked)"
                    class="btn-option-check"
                  />
                  <div class="btn-option-icon">
                    <img v-if="btn.icon" :src="btn.icon" :alt="btn.name" />
                    <div v-else class="btn-option-icon-placeholder"></div>
                  </div>
                  <div class="btn-option-text">
                    <span class="btn-option-name">{{ btn.name }}</span>
                    <span class="btn-option-desc">{{ btn.description }}</span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <!-- Point Counting status name labels (step 7 only) -->
          <div v-if="configStep === 7 && pointCountingStatusCount > 0" class="status-names-block">
            <label class="field-label">Status Names</label>
            <p class="field-desc">Enter a display label for each point-counting state (index 0 = alive/full).</p>
            <div v-for="i in pointCountingStatusCount" :key="i" class="status-name-row">
              <span class="status-name-index">{{ i - 1 }}</span>
              <input
                v-model="pointCountingStatusNames[i - 1]"
                type="text"
                class="field-input"
                :placeholder="['Alive', 'Dead', 'Fleeing', 'Decimated', 'Fleeing & Decimated'][i - 1] || 'label'"
              />
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class="nav-buttons">
          <button class="btn btn-secondary" @click="configBack">← Back</button>
          <template v-if="isAtSimpleEnd">
            <button class="btn btn-done" @click="openFinish">Done</button>
            <button class="btn btn-primary" @click="goAdvanced">Advanced config →</button>
          </template>
          <template v-else-if="firstDataNavSuppressed">
            <!-- MyFirstDataEditor handles its own navigation -->
          </template>
          <template v-else-if="configStep < configSteps.length - 1">
            <button class="btn btn-primary" @click="configNext">Next →</button>
          </template>
          <template v-else>
            <button class="btn btn-done" @click="openFinish">Done</button>
          </template>
        </div>
      </div>

      <!-- ─────────────── PHASE: firstdata ─────────────── -->
      <div v-else-if="phase === 'firstdata'" class="phase-firstdata">
        <MyFirstDataWizard
          :factions="firstData.factions"
          :sections="firstData.sections"
          :first-unit-name="firstData.firstUnitName"
          :first-unit-faction="firstData.firstUnitFaction"
          :first-unit-section="firstData.firstUnitSection"
          :profile-names="firstData.profileNames"
          :picked-skin="firstDataPickedSkin"
          @done="onFirstDataDone"
          @skip="onFirstDataSkip"
          @back="onFirstDataBack"
          @pick-skin="onPickSkin"
        />
      </div>

      <!-- ─────────────── PHASE: finish ─────────────── -->
      <div v-else-if="phase === 'finish'" class="phase-finish">
        <p class="phase-subtitle">Your game configuration is complete and ready to be uploaded!</p>

        <!-- ── GitHub Upload (always open) ── -->
        <div class="finish-section">
          <h3 class="finish-section-title">
            <svg class="gh-icon" viewBox="0 0 16 16" fill="currentColor"><path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>
            Upload to GitHub
          </h3>
          <p class="field-desc">
            You need a public GitHub repository to host your game files.
            <a href="https://docs.github.com/en/repositories/creating-and-managing-repositories/creating-a-new-repository" target="_blank" rel="noopener">How to create one</a>
          </p>

          <div class="field-row mt-sm">
            <label class="field-label">GitHub Repository</label>
            <input
              v-model="githubRepo"
              type="text"
              class="field-input"
              placeholder="owner/repo"
            />
            <p v-if="githubRepoError" class="field-error">{{ githubRepoError }}</p>
          </div>

          <div class="field-row mt-sm">
            <div class="label-with-help">
              <label class="field-label">Personal Access Token</label>
              <button class="help-btn" @click="showPatHelp = !showPatHelp" type="button">?</button>
            </div>
            <div v-if="showPatHelp" class="help-box">
              <ol>
                <li>Go to <a href="https://github.com/settings/tokens" target="_blank" rel="noopener">https://github.com/settings/tokens</a></li>
                <li>Click <strong>"Generate new token (classic)"</strong></li>
                <li>Give it a name, set an expiry, and tick <code>public_repo</code></li>
                <li>Click <strong>Generate token</strong> — copy it immediately, it won't be shown again</li>
              </ol>
            </div>
            <input
              v-model="githubToken"
              type="password"
              class="field-input"
              placeholder="ghp_…"
              autocomplete="off"
            />
            <p v-if="githubTokenError" class="field-error">{{ githubTokenError }}</p>
          </div>

          <button
            class="btn btn-primary mt-sm"
            :disabled="githubUploading"
            @click="uploadToGitHub"
          >{{ githubUploading ? 'Uploading…' : 'Upload to GitHub' }}</button>

          <!-- Progress bar -->
          <div v-if="githubUploading && uploadTotal > 0" class="upload-progress-wrap mt-sm">
            <div class="upload-progress-bar" :style="{ width: Math.round((uploadProgress / uploadTotal) * 100) + '%' }"></div>
            <span class="upload-progress-label">{{ uploadProgress }} / {{ uploadTotal }}</span>
          </div>

          <div v-if="githubSuccess" class="message message-success">
            {{ githubSuccess }}
            <a v-if="uploadFolderUrl" :href="uploadFolderUrl" target="_blank" rel="noopener" class="upload-folder-link">View folder on GitHub →</a>
          </div>
          <div v-if="githubError" class="message message-error">{{ githubError }}</div>
        </div>

        <!-- ── Publish to Warhall (create mode only) ── -->
        <div v-if="mode === 'create'" class="finish-section finish-section--publish">
          <button
            class="btn btn-add-game"
            :disabled="addGameLoading || !githubSuccess || !!addGameSuccess"
            @click="addGame"
          >{{ addGameLoading ? 'Publishing…' : 'Publish to Warhall' }}</button>
          <p v-if="!githubSuccess" class="field-desc mt-sm">Complete the GitHub upload above to enable this.</p>
          <div v-if="addGameError" class="message message-error">{{ addGameError }}</div>
        </div>

        <!-- ── Download Locally (collapsed at bottom) ── -->
        <div class="accordion-section">
          <button class="accordion-header" @click="expandedSection = expandedSection === 'download' ? null : 'download'" type="button">
            <span>💾 Download locally</span>
            <span class="accordion-arrow">{{ expandedSection === 'download' ? '▲' : '▼' }}</span>
          </button>
          <div v-if="expandedSection === 'download'" class="accordion-body">
            <button class="btn btn-done mt-sm" @click="downloadAllAsZip">
              ⬇ Download all files (zip)
            </button>
          </div>
        </div>

        <div class="nav-buttons" style="margin-top:1.5rem">
          <button class="btn btn-secondary" @click="$router.go(-1)">← Back to wizard</button>
          <button v-if="mode === 'edit'" class="btn btn-secondary" @click="startOver">↩ Back to game selector</button>
        </div>
      </div>

      <!-- ── Success modal ── -->
      <transition name="modal-fade">
        <div v-if="showSuccessModal" class="success-modal-overlay" @click.self="closeSuccessModal">
          <canvas ref="confettiCanvas" class="confetti-canvas"></canvas>
          <div class="success-modal">
            <div class="success-modal-icon">🎉</div>
            <h2 class="success-modal-title">Congratulations!</h2>
            <p class="success-modal-text">Your game is now in Warhall!</p>
            <button class="btn btn-done success-modal-close" @click="closeSuccessModal">Awesome!</button>
          </div>
        </div>
      </transition>

      <!-- ── Catalogue skin picker overlay ── -->
      <div v-if="showCataloguePicker" class="catalogue-picker-overlay">
        <div class="catalogue-picker-header">
          <button class="btn btn-secondary catalogue-picker-back" @click="showCataloguePicker = false">← Back to wizard</button>
          <span class="catalogue-picker-title">Pick a skin for <strong>{{ firstData.firstUnitName || 'your unit' }}</strong></span>
        </div>
        <div class="catalogue-picker-body">
          <Catalogue :skin-picker-mode="true" @skin-picked="onSkinPicked" />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
// Files inside public/SampleGame/ that are uploaded alongside gameconfig + manifest.
const SAMPLE_GAME_FILES = [
  'manifest.json',
  'bases.json',
  'cards.json',
  'dummylist.json',
  'equipment.json',
  'hierarchy.json',
  'options.json',
  'rules.json',
  'tokens.json',
  'version.json',
  'Sample Army/equipment.json',
  'Sample Army/modifiedprofile.json',
  'Sample Army/modifiedunit.json',
  'Sample Army/options.json',
  'Sample Army/profiles.json',
  'Sample Army/rules.json',
  'Sample Army/statlines.json',
  'Sample Army/units.json',
];

const FORMATION_OPTIONS = [
  { name: 'Cloud',                label: 'Cloud',                   description: 'Loose skirmish formation where soldiers spread freely',         icon: '/Images/FormationIcons/Cloud.png' },
  { name: 'CloudCheckerboard',    label: 'Cloud Checkerboard',      description: 'Cloud arranged in a checkerboard pattern',                      icon: '/Images/FormationIcons/CloudCheckerboard.png' },
  { name: 'CloudExtended',        label: 'Cloud Extended',          description: 'Cloud formation with extended spacing',                         icon: '/Images/FormationIcons/CloudExtended.png' },
  { name: 'CloudCompressed',      label: 'Cloud Compressed',        description: 'Cloud formation with compressed spacing',                       icon: '/Images/FormationIcons/CloudCompressed.png' },
  { name: 'RankAndFile',          label: 'Rank and File',           description: 'Standard ranked formation in rows and columns',                 icon: '/Images/FormationIcons/RankAndFile.png' },
  { name: 'RankAndFileSeparated', label: 'Rank and File Separated', description: 'Ranked formation but with separated models',                    icon: '/Images/FormationIcons/RankAndFileSeparated.png' },
  { name: 'Lance',                label: 'Lance',                   description: 'Triangular lance formation',                                    icon: '/Images/FormationIcons/Lance.png' },
];

const BUTTON_GROUPS = [
  {
    label: 'All items',
    buttons: [
      { name: 'Delete',           description: 'Delete the selected item',           icon: '/Images/WarhallGameButtonIcons/Delete.png' },
      { name: 'Radius',           description: 'Adjustable radius measurement tool',  icon: '/Images/WarhallGameButtonIcons/Radius.png' },
      { name: 'Random Direction', description: 'Random direction spinner',            icon: '/Images/WarhallGameButtonIcons/RandomDirection.png' },
    ],
  },
  {
    label: 'Soldiers & Units',
    buttons: [
      { name: 'Remove wounds',    description: 'Remove a wound marker from the unit',    icon: '/Images/WarhallGameButtonIcons/RemoveWounds.png' },
      { name: 'Add wounds',       description: 'Add a wound marker to the unit',         icon: '/Images/WarhallGameButtonIcons/AddWounds.png' },
      { name: 'Magnet',           description: 'Toggle magnet snap for placement',        icon: '/Images/WarhallGameButtonIcons/Magnet.png' },
      { name: 'Line of Sight',    description: 'Line of sight measurement tool',          icon: '/Images/WarhallGameButtonIcons/LineOfSight.png' },
      { name: 'Pivot decal',      description: 'Toggle pivot decal display',              icon: '/Images/WarhallGameButtonIcons/PivotDecal.png' },
      { name: 'Concentric lines', description: 'Toggle concentric range lines overlay',   icon: '/Images/WarhallGameButtonIcons/ConcentricLines.png' },
      { name: 'Base Size',        description: 'Change the base size of a model',         icon: '/Images/WarhallGameButtonIcons/BaseSize.png' },
      { name: 'Base Color',       description: 'Change the base color of a model',        icon: '/Images/WarhallGameButtonIcons/BaseColor.png' },
    ],
  },
  {
    label: 'Units only',
    buttons: [
      { name: 'Reform',                 description: 'Reform the unit formation',          icon: '/Images/WarhallGameButtonIcons/Reform.png' },
      { name: 'Reorder',                description: 'Reorder soldiers within the unit',   icon: '/Images/WarhallGameButtonIcons/Reorder.png' },
      { name: 'Align center to center', description: 'Align units center-to-center',       icon: '/Images/WarhallGameButtonIcons/AlignCenterToCenter.png' },
      { name: 'Duplicate',              description: 'Duplicate the selected item',        icon: '/Images/WarhallGameButtonIcons/Duplicate.png' },
      { name: 'Rotate 90',              description: 'Rotate the unit 90 degrees',         icon: '/Images/WarhallGameButtonIcons/Rotate90.png' },
    ],
  },
  {
    label: 'Card Stacks',
    buttons: [
      { name: 'Reset Card Stack',   description: 'Reset card stack to its original state', icon: '/Images/WarhallGameButtonIcons/ResetCardStack.png' },
      { name: 'Draw Card Stack',    description: 'Draw a card from the stack',              icon: '/Images/WarhallGameButtonIcons/DrawCardStack.png' },
      { name: 'Shuffle Card Stack', description: 'Shuffle the card stack randomly',         icon: '/Images/WarhallGameButtonIcons/ShuffleCardStack.png' },
    ],
  },
  {
    label: 'Cards',
    buttons: [
      { name: 'Flip Card',   description: 'Flip a card face up or face down', icon: '/Images/WarhallGameButtonIcons/FlipCard.png' },
      { name: 'Return Card', description: 'Return a card to its stack',        icon: '/Images/WarhallGameButtonIcons/ReturnCard.png' },
    ],
  },
];

const BUTTON_OPTIONS = BUTTON_GROUPS.flatMap(g => g.buttons);

const DEFAULT_SHOWN_BUTTONS = [
  'Delete', 'Radius',
  'Remove wounds', 'Add wounds', 'Magnet', 'Concentric lines', 'Base Size', 'Base Color',
  'Reform', 'Duplicate',
  'Reset Card Stack', 'Draw Card Stack', 'Shuffle Card Stack',
  'Flip Card', 'Return Card',
];

const CONFIG_STEPS = [
  {
    title: 'Main',
    fields: [
      { key: 'BattlegroundWidth',  label: 'Battleground Width (inches)',  description: 'Width of the battleground in inches.',                       type: 'number',    default: 72 },
      { key: 'BattlegroundHeight', label: 'Battleground Height (inches)', description: 'Height of the battleground in inches.',                      type: 'number',    default: 48 },
      { key: 'WoundsAttribute',    label: 'Wounds Attribute',             description: 'Attribute name in your game used to track wounds.',          type: 'string',    default: '', placeholder: 'E.g. "HP" or "W"', alphanumeric: true },
      { key: 'AvailableDice',      label: 'Available Dice',               description: 'Dice types shown in the dice panel for this game system.',   type: 'checklist', default: ['D6', 'D3'], options: ['D6', 'D3', 'D8', 'D10', 'Artillery', 'Scatter', 'RandomDirection', 'Pair', 'Trio', 'Order'], icons: { D6: '/Images/Dice/Icon_6_1.png', D3: '/Images/Dice/Icon_3.png', D8: '/Images/Dice/Icon_8_1.png', D10: '/Images/Dice/Icon_10.png', Artillery: '/Images/Dice/Icon_artillery.png', Scatter: '/Images/Dice/Icon_scatter.png', RandomDirection: '/Images/Dice/Icon_Randomdir.png', Pair: '/Images/Dice/Icon_block dice copia.png', Trio: '/Images/Dice/Icon_block dice copia 2.png', Order: '/Images/Dice/Dice_secuence.png' } },
    ],
  },
  {
    title: 'Formations',
    fields: [
      { key: 'DefaultFormation',            label: 'Default Formation',           description: 'Default formation type for units.',                                                                    type: 'string', default: 'RankAndFile' },
      { key: 'PivotDistanceMeasurement',    label: 'Pivot Distance Measurement',  description: 'How pivot distance is measured: Straight = chord distance, Arc = arc length.',                        type: 'select', default: 'Straight', options: [{ value: 'Straight', label: 'Straight (chord)' }, { value: 'Arc', label: 'Arc (arc length)' }] },
      { key: 'MaxCloudSeparation',          label: 'Max Cloud Separation (in.)',  description: 'Maximum separation between cloud soldiers before showing a cohesion violation.',                      type: 'float',  default: 1.0 },
      { key: 'DefaultSoldierSelectionMode', label: 'Individual Soldier Selection', description: 'Off = select the whole unit, On = select individual soldiers.',                                     type: 'bool',   default: false },
    ],
  },
  {
    title: 'Tokens',
    intro: 'In this section you can add any kind of tokens that your game might need.',
    fields: [],
  },
  {
    title: 'Terrain',
    intro: 'In this section you can add terrain elements to your game.',
    fields: [],
  },
  {
    title: 'Deployments',
    intro: 'In this section you can add deployment lines your game might need.',
    fields: [],
  },
  {
    title: 'Objectives',
    intro: 'If your game uses objectives, here you can configure where they are located on the battlefield and what they are called.',
    fields: [],
  },
  {
    title: 'My First Data',
    createOnly: true,
    fields: [],
  },
  {
    title: 'Cards',
    intro: 'If your game uses cards, here you can configure what the default cards will look like.',
    fields: [
      { key: 'Card_Width',        label: 'Card Width',       description: 'Default card width in millimetres.',                             type: 'float',           default: 50.0 },
      { key: 'Card_Length',       label: 'Card Length',      description: 'Default card length in millimetres.',                            type: 'float',           default: 50.0 },
      { key: 'Card_IsRound',      label: 'Round Card',       description: 'Display the card token as a circle instead of a rectangle.',     type: 'bool',            default: true },
      { key: 'Card_ShowInitials', label: 'Show Initials',    description: 'Show initials of the card name on the card token.',              type: 'bool',            default: true },
      { key: 'Card_Border',       label: 'Border',           description: 'Draw a border around the card token.',                           type: 'bool',            default: true },
      { key: 'Card_TextColor',    label: 'Text Color',       description: 'Text color for initials on the card.',                           type: 'colorpicker-hex', default: '#000000' },
      { key: 'Card_Color',        label: 'Background Color', description: 'Background fill color for the card token.',                      type: 'colorpicker-rgb', default: 'rgb(163, 163, 163)' },
      { key: 'Card_BorderColor',  label: 'Border Color',     description: 'Border color for the card token.',                               type: 'colorpicker-hex', default: '#000000' },
    ],
  },
  {
    title: 'Algorithms',
    fields: [
      { key: 'ResultCalculatorAlgorithm', label: 'Result Calculator',      description: 'Which combat result calculator to use at game end.',  type: 'select', default: 0, options: [{ value: 0, label: 'Disabled' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }] },
      { key: 'ListParsingAlgorithm',      label: 'List Parsing Algorithm', description: 'Algorithm used when parsing army lists.',              type: 'select', default: 0, options: [{ value: 0, label: 'Default' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }] },
    ],
  },
  {
    title: 'Point Counting',
    fields: [
      { key: 'PointCounting', label: 'Point Counting Mode', description: 'In your game, do you get points for killing units? In what quantity?', type: 'select', default: 'Disabled', options: [{ value: 'Disabled', label: 'Disabled' }, { value: 'FullOrNone', label: 'Full or None' }, { value: 'FullHalfNone', label: 'Full / Half / None' }, { value: 'Graduated', label: 'Graduated (5 states)' }] },
    ],
  },
  {
    title: 'Secondary',
    fields: [
      { key: 'UseTurnController',  label: 'Use Turn Controller',  description: 'Activates the turn controller for this game system.',                      type: 'bool', default: false },
      { key: 'DefaultZenithLight', label: 'Default Zenith Light', description: 'Scene light mode: Off = original light, On = zenith overhead light.',      type: 'bool', default: false },
    ],
  },
  {
    title: 'Buttons',
    fields: [],
  },
];

function buildDefaultConfig() {
  const cfg = {};
  for (const step of CONFIG_STEPS) {
    for (const field of step.fields) {
      cfg[field.key] = field.default;
    }
  }
  return cfg;
}

import SkinGrid from '@/components/SkinGrid.vue';
import TokensEditor from '@/components/TokensEditor.vue';
import TerrainEditor from '@/components/TerrainEditor.vue';
import DeploymentsEditor from '@/components/DeploymentsEditor.vue';
import ObjectivesEditor from '@/components/ObjectivesEditor.vue';
import MyFirstDataEditor from '@/components/MyFirstDataEditor.vue';
import MyFirstDataWizard from '@/components/MyFirstDataWizard.vue';
import Catalogue from '@/views/Catalogue.vue';

/** Encode a UTF-8 string to base64 (safe for non-ASCII). */
function toBase64(str) {
  const bytes = new TextEncoder().encode(str);
  const binary = Array.from(bytes, (b) => String.fromCodePoint(b)).join('');
  return btoa(binary);
}

/** Parse "owner/repo" from a raw GitHub URL or short form. Returns null if unrecognisable. */
function parseOwnerRepo(input) {
  const clean = input.trim().replace(/\.git$/, '').replace(/\/$/, '');
  const m = clean.match(/(?:https?:\/\/github\.com\/)?([A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+)/);
  return m ? m[1] : null;
}

export default {
  name: 'GameConfigWizard',

  components: { SkinGrid, TokensEditor, TerrainEditor, DeploymentsEditor, ObjectivesEditor, MyFirstDataEditor, MyFirstDataWizard, Catalogue },

  props: {
    mode: {
      type: String,
      default: 'create', // 'create' | 'edit'
    },
    existingConfig: {
      type: Object,
      default: null,
    },
    initialGameName: {
      type: String,
      default: '',
    },
    existingDataBasePath: {
      type: String,
      default: '',
    },
    existingGameId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      // Phases: 'name' | 'config' | 'finish'
      phase: 'name',

      // Wizard mode: 'simple' (steps 0-1 only) | 'advanced' (steps 2-6) | 'full' (all, used in edit)
      wizardMode: 'simple',

      // Phase: name
      gameName: '',
      gameNameError: '',

      // Phase: config
      configSteps: this.mode === 'create' ? CONFIG_STEPS : CONFIG_STEPS.filter(s => !s.createOnly),
      configStep: 0,
      config: buildDefaultConfig(),

      // Phase: config ─ Point Counting status names (one per state, max 5 for Graduated)
      pointCountingStatusNames: ['Alive', 'Dead', 'Fleeing', 'Decimated', 'Fleeing & Decimated'],

      // Phase: config ─ Tokens step (managed by TokensEditor component)
      tokensJson: [],
      tokenCatalogue: [], // full skin catalogue received from TokensEditor via catalogue-loaded

      // Phase: config ─ Terrain step (managed by TerrainEditor component)
      terrainsJson: [],

      // Phase: config ─ Deployments step (managed by DeploymentsEditor component)
      deploymentsJson: [],

      // Phase: config ─ Objectives step (managed by ObjectivesEditor component)
      objectivesJson: [],

      // Phase: config ─ My First Data step
      firstData: { factions: [], sections: [], firstUnitName: '', firstUnitFaction: '', firstUnitSection: '', profileNames: [] },
      // Normalized JSON snapshots keyed by filename, populated in edit mode
      originalFiles: {},
      firstDataComplete: false,
      firstDataState: 'question', // 'question' | 'no-answered' | 'wizard' | 'wizard-done'
      firstDataPickedSkin: null,  // skin picked via the catalogue picker in step 6
      showCataloguePicker: false,

      // Phase: config ─ Buttons step
      buttonGroups: BUTTON_GROUPS,
      buttonOptions: BUTTON_OPTIONS,
      shownButtons: DEFAULT_SHOWN_BUTTONS.slice(),

      // Phase: config ─ Formations sub-wizard
      selectedFormations: [],
      formationSubStep: 0,
      formationError: '',
      formationOptions: FORMATION_OPTIONS,
      formationChangingRules: [],

      // Phase: finish – GitHub
      showPatHelp: false,
      expandedSection: null,
      githubRepo: '',
      githubRepoError: '',
      githubToken: '',
      githubTokenError: '',
      githubUploading: false,
      githubSuccess: '',
      githubError: '',
      uploadProgress: 0,
      uploadTotal: 0,
      uploadFolderUrl: '',
      // Phase: finish – Add Game (create mode only)
      addGameLoading: false,
      addGameSuccess: '',
      addGameError: '',
      nextGameId: null,
      showSuccessModal: false,
    };
  },

  computed: {
    currentConfigStep() {
      return this.configSteps[this.configStep];
    },

    simpleStepCount() {
      return this.mode === 'create' ? 7 : 6;
    },

    displaySteps() {
      if (this.wizardMode === 'simple') return this.configSteps.slice(0, this.simpleStepCount);
      if (this.wizardMode === 'advanced') return this.configSteps.slice(this.simpleStepCount);
      return this.configSteps; // 'full' — edit mode
    },

    displayStepOffset() {
      return this.wizardMode === 'advanced' ? this.simpleStepCount : 0;
    },

    // True when we are at the last step of the simple wizard (My First Data in create, Objectives in edit)
    isAtSimpleEnd() {
      if (this.wizardMode !== 'simple' || this.configStep !== this.simpleStepCount - 1) return false;
      if (this.mode !== 'create') return true;
      return this.firstDataState === 'no-answered' || this.firstDataState === 'wizard-done';
    },

    // True while the My First Data editor owns its own navigation (question or wizard active)
    firstDataNavSuppressed() {
      return this.mode === 'create' &&
        this.configStep === this.simpleStepCount - 1 &&
        this.firstDataState === 'question';
    },
    pointCountingStatusCount() {
      const counts = { Disabled: 0, FullOrNone: 2, FullHalfNone: 3, Graduated: 5 };
      return counts[this.config.PointCounting] || 0;
    },
    /** Token skin objects (with thumbnailUrl) for the currently selected tokens */
    selectedTokenSkins() {
      return this.tokenCatalogue.filter(s =>
        this.tokensJson.some(t => String(t.SkinId) === String(s.id))
      );
    },
    formationSubStepLabel() {
      const def = this.config.DefaultFormation;
      const hasParams = def === 'RankAndFile' || def === 'Cloud';
      const singleFormation = this.selectedFormations.length === 1;
      // When only one formation: skip sub-step 1, so total is 1 (no params) or 2 (with params)
      // When multiple formations: total is 2 (no params) or 3 (with params)
      const total = singleFormation ? (hasParams ? 2 : 1) : (hasParams ? 3 : 2);
      const labels = [
        'Select available formations',
        'Set default formation',
        'Configure default parameters',
      ];
      // Map visible sub-step index: sub-step 2 becomes display index 2 normally, 1 if single
      let displayIdx;
      if (this.formationSubStep === 0) displayIdx = 0;
      else if (this.formationSubStep === 1) displayIdx = 1;
      else displayIdx = singleFormation ? 1 : 2; // sub-step 2
      return `Formations — Step ${displayIdx + 1} / ${total}: ${labels[this.formationSubStep]}`;
    },
    gameIdPreview() {
      return this.nextGameId !== null ? this.nextGameId : '…';
    },
    addGameDataPath() {
      const ownerRepo = parseOwnerRepo(this.githubRepo);
      if (!ownerRepo) return '';
      const safeName = this.gameName.trim().replace(/\s+/g, '_');
      return `https://raw.githubusercontent.com/${ownerRepo}/refs/heads/main/${safeName}/manifest.json`;
    },
    progressPercent() {
      return Math.round(((this.configStep + 1) / this.configSteps.length) * 100);
    },
  },

  mounted() {
    if (this.mode === 'edit') {
      this.wizardMode = 'full';
    }
    this.syncFromRoute(this.$route.query);
    if (this.existingConfig) {
      this.loadFromJson(this.existingConfig, this.initialGameName);
      if (this.mode === 'edit' && this.existingDataBasePath) {
        this.loadExistingGameFiles();
      }
      if (this.phase !== 'config') {
        this.$router.push({ query: { phase: 'config', step: 'main' } });
      }
    }
  },

  watch: {
    $route(to) {
      this.syncFromRoute(to.query);
    },
  },

  methods: {
    showHelpAlert() {
      alert('If you need help adding your game: we can help!\nWrite us on info@warhall.eu and we will reach out asap!');
    },

    syncFromRoute(q) {
      if (q.phase === 'config') {
        this.phase = 'config';
        const slug = q.step || '';
        const bySlug = this.configSteps.findIndex(s => s.title.toLowerCase().replace(/\s+/g, '-') === slug);
        const byIndex = parseInt(slug, 10);
        let newStep;
        if (bySlug !== -1) newStep = bySlug;
        else if (!isNaN(byIndex)) newStep = Math.min(byIndex, this.configSteps.length - 1);
        else newStep = 0;
        if (newStep !== this.configStep) {
          this.formationSubStep = 0;
          this.formationError = '';
        }
        this.configStep = newStep;
        // Reset My First Data state when navigating away from that step
        if (this.mode === 'create' && newStep !== 6) {
          this.firstDataState = 'question';
        }
        // Keep wizardMode in sync with the current step (create mode only)
        if (this.mode === 'create') {
          if (newStep >= this.simpleStepCount && this.wizardMode === 'simple') {
            this.wizardMode = 'advanced';
          } else if (newStep < this.simpleStepCount && this.wizardMode === 'advanced') {
            this.wizardMode = 'simple';
          }
        }
      } else if (q.phase === 'finish') {
        this.phase = 'finish';
      } else {
        this.phase = 'name';
      }
    },

    // ── Edit mode: load existing config into wizard state ────────
    loadFromJson(json, name) {
      const defaults = buildDefaultConfig();
      const cfg = { ...defaults };

      // Main
      cfg.BattlegroundWidth  = json.BattlegroundWidth  ?? defaults.BattlegroundWidth;
      cfg.BattlegroundHeight = json.BattlegroundHeight ?? defaults.BattlegroundHeight;
      cfg.WoundsAttribute    = json.WoundsAttribute    ?? defaults.WoundsAttribute;
      cfg.AvailableDice      = Array.isArray(json.AvailableDice) ? json.AvailableDice.slice() : defaults.AvailableDice;

      // Formations
      cfg.DefaultFormation            = json.DefaultFormation            ?? defaults.DefaultFormation;
      cfg.PivotDistanceMeasurement    = json.PivotDistanceMeasurement    ?? defaults.PivotDistanceMeasurement;
      cfg.MaxCloudSeparation          = json.MaxCloudSeparation          ?? defaults.MaxCloudSeparation;
      cfg.DefaultSoldierSelectionMode = json.DefaultSoldierSelectionMode ?? defaults.DefaultSoldierSelectionMode;

      // Cards
      const dc = json.DefaultCardConfig || {};
      cfg.Card_Width        = dc.Width        ?? defaults.Card_Width;
      cfg.Card_Length       = dc.Length       ?? defaults.Card_Length;
      cfg.Card_IsRound      = dc.IsRound      ?? defaults.Card_IsRound;
      cfg.Card_ShowInitials = dc.ShowInitials ?? defaults.Card_ShowInitials;
      cfg.Card_Border       = dc.Border       ?? defaults.Card_Border;
      cfg.Card_TextColor    = dc.TextColor    ?? defaults.Card_TextColor;
      cfg.Card_Color        = dc.Color        ?? defaults.Card_Color;
      cfg.Card_BorderColor  = dc.BorderColor  ?? defaults.Card_BorderColor;

      // Algorithms
      cfg.ResultCalculatorAlgorithm = json.ResultCalculatorAlgorithm ?? defaults.ResultCalculatorAlgorithm;
      cfg.ListParsingAlgorithm      = json.ListParsingAlgorithm      ?? defaults.ListParsingAlgorithm;

      // Point Counting
      cfg.PointCounting = json.PointCounting ?? defaults.PointCounting;

      // Secondary
      cfg.UseTurnController  = json.UseTurnController  ?? defaults.UseTurnController;
      cfg.DefaultZenithLight = json.DefaultZenithLight ?? defaults.DefaultZenithLight;

      this.config = cfg;
      this.gameName = name;

      // Formations list
      this.selectedFormations = Array.isArray(json.Formations)
        ? json.Formations.slice().sort((a, b) => a.Index - b.Index).map(f => f.Type)
        : [];

      // Formation changing rules
      this.formationChangingRules = Array.isArray(json.FormationChangingRules)
        ? json.FormationChangingRules.map(r => ({ Rule: r.Rule, FormationType: r.FormationType }))
        : [];

      // Buttons
      this.shownButtons = Array.isArray(json.ShownButtons)
        ? json.ShownButtons.slice()
        : DEFAULT_SHOWN_BUTTONS.slice();

      // Point counting status names
      const defaultNames = ['Alive', 'Dead', 'Fleeing', 'Decimated', 'Fleeing & Decimated'];
      const nameArr = defaultNames.slice();
      if (Array.isArray(json.PointCountingStatusNames)) {
        for (const entry of json.PointCountingStatusNames) {
          if (entry.Status >= 0 && entry.Status < nameArr.length) {
            nameArr[entry.Status] = entry.Name;
          }
        }
      }
      this.pointCountingStatusNames = nameArr;
    },

    // ── Edit mode: pre-load existing json files ───────────────
    async loadExistingGameFiles() {
      if (!this.existingDataBasePath) return;
      const filesToLoad = [
        'tokens.json',
        'terrains.json',
        'deployments.json',
        'objectives.json',
        'rules.json',
        'bases.json',
        'hierarchy.json',
      ];
      try {
        const responses = await Promise.all(
          filesToLoad.map(f => fetch(this.existingDataBasePath + f).catch(() => null))
        );
        for (let i = 0; i < filesToLoad.length; i++) {
          const res = responses[i];
          if (!res || !res.ok) continue;
          const text = await res.text();
          // Store normalized snapshot for change detection
          try { this.originalFiles[filesToLoad[i]] = JSON.stringify(JSON.parse(text)); } catch (_) {}
          // Pre-populate reactive state for editable files
          try {
            const parsed = JSON.parse(text);
            if (filesToLoad[i] === 'tokens.json')      this.tokensJson      = parsed;
            if (filesToLoad[i] === 'terrains.json')    this.terrainsJson    = parsed;
            if (filesToLoad[i] === 'deployments.json') this.deploymentsJson = parsed;
            if (filesToLoad[i] === 'objectives.json')  this.objectivesJson  = parsed;
          } catch (_) {}
        }
      } catch (_) {
        // silent — files just won't be pre-populated
      }
    },

    // ── Build helpers (now just return the component-managed arrays) ──
    buildTokensJson() { return this.tokensJson; },
    buildTerrainJson() { return this.terrainsJson; },
    buildDeploymentsJson() { return this.deploymentsJson; },
    buildObjectivesJson() { return this.objectivesJson; },

    // ── Name phase ──────────────────────────────────────────────
    startWizard() {
      this.gameNameError = '';
      const name = this.gameName.trim();
      if (!name) {
        this.gameNameError = 'Game name is required.';
        return;
      }
      if (/[\\/:*?"<>|]/.test(name)) {
        this.gameNameError = 'Game name contains invalid characters (\\ / : * ? " < > |).';
        return;
      }
      this.$router.push({ query: { phase: 'config', step: 'main' } });
    },

    // ── Config phase ────────────────────────────────────────────
    configNext() {
      if (this.configStep === 1) {
        this.formationError = '';
        if (this.formationSubStep === 0) {
          if (this.selectedFormations.length === 0) {
            this.formationError = 'Please select at least one formation.';
            return;
          }
          if (!this.selectedFormations.includes(this.config.DefaultFormation)) {
            this.config.DefaultFormation = this.selectedFormations[0];
          }
          // Skip 'Set default' if only one formation is available
          if (this.selectedFormations.length === 1) {
            const def = this.config.DefaultFormation;
            if (def === 'RankAndFile' || def === 'Cloud') {
              this.formationSubStep = 2;
            } else {
              const slug = this.configSteps[2].title.toLowerCase().replace(/\s+/g, '-');
              this.$router.push({ query: { phase: 'config', step: slug } });
            }
          } else {
            this.formationSubStep = 1;
          }
        } else if (this.formationSubStep === 1) {
          const def = this.config.DefaultFormation;
          if (def === 'RankAndFile' || def === 'Cloud') {
            this.formationSubStep = 2;
          } else {
            const slug = this.configSteps[2].title.toLowerCase().replace(/\s+/g, '-');
            this.$router.push({ query: { phase: 'config', step: slug } });
          }
        } else if (this.formationSubStep === 2) {
          const slug = this.configSteps[2].title.toLowerCase().replace(/\s+/g, '-');
          this.$router.push({ query: { phase: 'config', step: slug } });
        }
        return;
      }
      const next = this.configStep + 1;
      if (next < this.configSteps.length) {
        const slug = this.configSteps[next].title.toLowerCase().replace(/\s+/g, '-');
        this.$router.push({ query: { phase: 'config', step: slug } });
      }
    },

    configBack() {
      if (this.configStep === 1 && this.formationSubStep > 0) {
        // Skip 'Set default' sub-step when going back if only one formation
        if (this.formationSubStep === 2 && this.selectedFormations.length === 1) {
          this.formationSubStep = 0;
        } else {
          this.formationSubStep--;
        }
        return;
      }
      // When leaving the first advanced step, drop back to simple mode
      if (this.configStep === this.simpleStepCount && this.wizardMode === 'advanced') {
        this.wizardMode = 'simple';
      }
      this.$router.go(-1);
    },

    goAdvanced() {
      this.wizardMode = 'advanced';
      const slug = this.configSteps[this.simpleStepCount].title.toLowerCase().replace(/\s+/g, '-');
      this.$router.push({ query: { phase: 'config', step: slug } });
    },

    openFinish() {
      this.githubSuccess = '';
      this.githubError = '';
      this.addGameSuccess = '';
      this.addGameError = '';
      this.nextGameId = null;
      this.$router.push({ query: { phase: 'finish' } });
      if (this.mode === 'create') {
        fetch('/NextGameId/', { credentials: 'include' })
          .then(r => r.json())
          .then(d => { if (d.success) this.nextGameId = d.nextId; })
          .catch(() => {});
      }
    },

    // ── Finish phase – download ─────────────────────────────────
    downloadJson() {
      const json = JSON.stringify(this.buildJson(), null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'gameconfig.json';
      a.click();
      URL.revokeObjectURL(url);
    },

    downloadTokensJson() {
      const json = JSON.stringify(this.buildTokensJson(), null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tokens.json';
      a.click();
      URL.revokeObjectURL(url);
    },

    downloadTerrainJson() {
      const json = JSON.stringify(this.buildTerrainJson(), null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'terrains.json';
      a.click();
      URL.revokeObjectURL(url);
    },

    downloadObjectivesJson() {
      const json = JSON.stringify(this.buildObjectivesJson(), null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'objectives.json';
      a.click();
      URL.revokeObjectURL(url);
    },

    // ── Finish phase – GitHub ────────────────────────────────────
    async uploadToGitHub() {
      this.githubRepoError = '';
      this.githubTokenError = '';
      this.githubSuccess = '';
      this.githubError = '';
      this.uploadProgress = 0;
      this.uploadTotal = 0;
      this.uploadFolderUrl = '';

      const ownerRepo = parseOwnerRepo(this.githubRepo);
      if (!ownerRepo) {
        this.githubRepoError = 'Enter a valid repository, e.g. my-org/warhall-data or https://github.com/my-org/warhall-data';
        return;
      }
      if (!this.githubToken.trim()) {
        this.githubTokenError = 'A Personal Access Token is required.';
        return;
      }

      this.githubUploading = true;
      this.uploadProgress = 0;
      this.uploadTotal = 0;
      this.uploadFolderUrl = '';
      const safeName = this.gameName.trim().replace(/\s+/g, '_');
      const baseDir = encodeURIComponent(safeName);
      const repoBase = `https://api.github.com/repos/${ownerRepo}/contents/${baseDir}`;
      const jsonPayload = this.buildJson();
      const content = toBase64(JSON.stringify(jsonPayload, null, 2));
      const headers = {
        Authorization: `Bearer ${this.githubToken.trim()}`,
        'Content-Type': 'application/json',
        Accept: 'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
      };

      // ── Build the faction folder name ────────────────────────
      const factionFolder = this.firstData.firstUnitFaction
        ? this.firstData.firstUnitFaction.trim().replace(/\s+/g, '_')
        : 'Sample_Army';

      try {
        let allFiles = [];

        if (this.mode === 'edit') {
          // ── Edit mode: only push gameconfig + files that actually changed ──

          // Helper: returns true when the built content differs from what was loaded
          const hasChanged = (filename, builtContent) => {
            const original = this.originalFiles[filename];
            if (!original) return true; // file didn't exist yet — treat as new
            try { return JSON.stringify(JSON.parse(builtContent)) !== original; } catch (_) { return true; }
          };

          // gameconfig.json — always push (the whole point of ModifyGame)
          allFiles.push(['gameconfig.json', JSON.stringify(this.buildJson(), null, 2)]);

          // Files controlled by the wizard editors
          const candidates = [
            ['tokens.json',      JSON.stringify(this.buildTokensJson(), null, 2)],
            ['terrains.json',    JSON.stringify(this.buildTerrainJson(), null, 2)],
            ['deployments.json', JSON.stringify(this.buildDeploymentsJson(), null, 2)],
            ['objectives.json',  JSON.stringify(this.buildObjectivesJson(), null, 2)],
          ];

          // Files from "My First Data" — only push if user completed that section
          if (this.firstDataComplete) {
            candidates.push(['rules.json',     JSON.stringify(this.buildGameRulesJson(), null, 2)]);
            candidates.push(['bases.json',     JSON.stringify(this.buildBasesJson(), null, 2)]);
            candidates.push(['hierarchy.json', JSON.stringify(this.buildHierarchyJson(), null, 2)]);
            const ff = factionFolder;
            candidates.push([`${ff}/units.json`,           JSON.stringify(this.buildFirstUnitsJson(), null, 2)]);
            candidates.push([`${ff}/profiles.json`,        JSON.stringify(this.buildFirstProfilesJson(), null, 2)]);
            candidates.push([`${ff}/rules.json`,           JSON.stringify(this.buildFactionRulesJson(), null, 2)]);
            candidates.push([`${ff}/statlines.json`,       JSON.stringify(this.buildFactionStatlinesJson(), null, 2)]);
          }

          for (const [filename, builtContent] of candidates) {
            if (hasChanged(filename, builtContent)) allFiles.push([filename, builtContent]);
          }

          // Fetch existing manifest and increment its version
          let manifestObj = { version: 1, files: ['gameconfig.json', 'manifest.json'] };
          if (this.existingDataBasePath) {
            try {
              const mRes = await fetch(this.existingDataBasePath + 'manifest.json');
              if (mRes.ok) {
                const existing = await mRes.json();
                manifestObj = { ...existing, version: (existing.version || 1) + 1 };
              }
            } catch (_) { /* keep default */ }
          }
          allFiles.push(['manifest.json', JSON.stringify(manifestObj, null, 2)]);

        } else {
          // ── Create mode: upload all files ────────────────────────
          allFiles.push(['gameconfig.json',  JSON.stringify(this.buildJson(), null, 2)]);
          allFiles.push(['tokens.json',      JSON.stringify(this.buildTokensJson(), null, 2)]);
          allFiles.push(['terrains.json',    JSON.stringify(this.buildTerrainJson(), null, 2)]);
          allFiles.push(['objectives.json',  JSON.stringify(this.buildObjectivesJson(), null, 2)]);
          allFiles.push(['deployments.json', JSON.stringify(this.buildDeploymentsJson(), null, 2)]);
          allFiles.push(['rules.json',       JSON.stringify(this.firstDataComplete ? this.buildGameRulesJson()  : [], null, 2)]);
          allFiles.push(['bases.json',       JSON.stringify(this.firstDataComplete ? this.buildBasesJson()      : [], null, 2)]);
          allFiles.push(['hierarchy.json',   JSON.stringify(this.buildHierarchyJson(), null, 2)]);
          allFiles.push(['equipment.json',   '[]']);
          allFiles.push(['options.json',     '[]']);
          allFiles.push(['cards.json',       '[]']);
          allFiles.push(['version.json',     JSON.stringify({ version: 1, url: '' })]);
          allFiles.push(['dummylist.json',   '""']);

          if (this.firstDataComplete) {
            allFiles.push([`${factionFolder}/units.json`,            JSON.stringify(this.buildFirstUnitsJson(), null, 2)]);
            allFiles.push([`${factionFolder}/profiles.json`,         JSON.stringify(this.buildFirstProfilesJson(), null, 2)]);
            allFiles.push([`${factionFolder}/rules.json`,            JSON.stringify(this.buildFactionRulesJson(), null, 2)]);
            allFiles.push([`${factionFolder}/statlines.json`,        JSON.stringify(this.buildFactionStatlinesJson(), null, 2)]);
            allFiles.push([`${factionFolder}/equipment.json`,        '[]']);
            allFiles.push([`${factionFolder}/options.json`,          '[]']);
            allFiles.push([`${factionFolder}/modifiedprofile.json`,  '[]']);
            allFiles.push([`${factionFolder}/modifiedunit.json`,     '[]']);
          }

          // Manifest lists all files uploaded above
          allFiles.push(['manifest.json', JSON.stringify({ version: 1, files: allFiles.map(([p]) => p) }, null, 2)]);
        }

        this.uploadTotal = allFiles.length;

        // ── Upload each file ──────────────────────────────────────
        for (const [filePath, fileContent] of allFiles) {
          const encodedPath = filePath.split('/').map(encodeURIComponent).join('/');
          const fileUrl = `${repoBase}/${encodedPath}`;
          let existingSha;
          const getFile = await fetch(fileUrl, { headers });
          if (getFile.ok) {
            existingSha = (await getFile.json()).sha;
          } else if (getFile.status !== 404) {
            const err = await getFile.json();
            throw new Error(err.message || `GitHub API error ${getFile.status}`);
          }
          const putRes = await fetch(fileUrl, {
            method: 'PUT',
            headers,
            body: JSON.stringify({
              message: `${this.mode === 'edit' ? 'Update' : 'Add'} ${filePath} for ${safeName}`,
              content: toBase64(fileContent),
              ...(existingSha ? { sha: existingSha } : {}),
            }),
          });
          if (!putRes.ok) {
            const err = await putRes.json();
            throw new Error(err.message || `GitHub API error ${putRes.status}`);
          }
          this.uploadProgress += 1;
        }

        const ownerRepoFinal_PLACEHOLDER = parseOwnerRepo(this.githubRepo);
        this.uploadFolderUrl = `https://github.com/${ownerRepoFinal_PLACEHOLDER}/tree/main/${safeName}`;
        this.githubSuccess = 'Upload complete!';

        // Sync Item/ItemInstance/Costume in DB after a successful edit-mode push
        if (this.mode === 'edit' && this.existingGameId) {
          try {
            await fetch('/SyncGameSkins/', {
              method: 'POST',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                gameId:   this.existingGameId,
                tokens:   this.buildTokensJson(),
                terrains: this.buildTerrainJson(),
              }),
            });
          } catch (_) {
            // DB sync is best-effort; GitHub push already succeeded
          }
        }
      } catch (err_DONE) {
        this.githubError = 'Upload failed: ' + err_DONE.message;
      } finally {
        this.githubUploading = false;
      }
    },

    // ── My First Data JSON builders ─────────────────────────────
    buildHierarchyJson() {
      const { firstUnitName, firstUnitFaction, firstUnitSection } = this.firstData;
      const result = { Factions: {} };
      if (firstUnitName && firstUnitFaction) {
        const unitId = 'unit_' + firstUnitName.trim().toLowerCase().replace(/\s+/g, '_');
        const faction = firstUnitFaction.trim();
        const section = (firstUnitSection && firstUnitSection.trim()) || faction;
        result.Factions[faction] = {};
        result.Factions[faction][section] = [unitId];
      }
      return result;
    },

    buildFirstUnitsJson() {
      const { firstUnitName, profileNames } = this.firstData;
      if (!firstUnitName) return [];
      const unitId = 'unit_' + firstUnitName.trim().toLowerCase().replace(/\s+/g, '_');
      return [{
        Id: unitId,
        Name: firstUnitName.trim(),
        Type: 'Unit',
        Description: '',
        BasicProfileIds: profileNames.map(n => 'prof_' + n.trim().toLowerCase().replace(/\s+/g, '_')),
        Children: [],
        DefaultFront: 5,
        Formation: 0,
      }];
    },

    buildFirstProfilesJson() {
      const { profileNames, firstStatlineStats, baseWidth, baseLength } = this.firstData;
      const hasBase = baseWidth || baseLength;
      const hasStats = firstStatlineStats && Object.keys(firstStatlineStats).length > 0;
      return profileNames.map((n, idx) => {
        const safeId = n.trim().toLowerCase().replace(/\s+/g, '_');
        const children = [];
        if (idx === 0 && hasStats) children.push({ Id: `stat_${safeId}` });
        return {
          Id: 'prof_' + safeId,
          Name: n.trim(),
          Type: 'Profile',
          BaseId: (idx === 0 && hasBase) ? `base_${safeId}` : '',
          Children: children,
        };
      });
    },

    buildBasesJson() {
      const { baseShape, baseWidth, baseLength, profileNames, firstUnitName } = this.firstData;
      if (!baseWidth && !baseLength) return [];
      const name = (profileNames[0] || firstUnitName || 'Profile').trim();
      const safeId = name.toLowerCase().replace(/\s+/g, '_');
      const shapeNames = ['', 'Round', 'Square'];
      return [{
        Id: `base_${safeId}`,
        Name: `${name} (${baseWidth || 0}x${baseLength || 0}mm${baseShape ? ' ' + shapeNames[baseShape] : ''})`,
        Shape: baseShape || 0,
        Width: Number(baseWidth) || 0,
        Length: Number(baseLength) || 0,
      }];
    },

    buildGameRulesJson() {
      const { firstRule, firstRuleDescription } = this.firstData;
      if (!firstRule) return [];
      const safeId = firstRule.trim().toLowerCase().replace(/\s+/g, '_');
      return [{ Id: `rule_${safeId}`, Name: firstRule.trim(), Type: 'Rule', Description: firstRuleDescription || '' }];
    },

    buildFactionRulesJson() {
      return this.buildGameRulesJson();
    },

    buildFactionStatlinesJson() {
      const { firstStatlineStats, profileNames, firstUnitName, firstRule } = this.firstData;
      if (!firstStatlineStats || !Object.keys(firstStatlineStats).length) return [];
      const profileName = (profileNames[0] || firstUnitName || 'Profile').trim();
      const safeId = profileName.toLowerCase().replace(/\s+/g, '_');
      const ruleId = firstRule ? `rule_${firstRule.trim().toLowerCase().replace(/\s+/g, '_')}` : null;
      return [{
        Id: `stat_${safeId}`,
        Name: profileName,
        Type: 'Statline',
        Attributes: { ...firstStatlineStats },
        Children: ruleId ? [{ Id: ruleId }] : [],
      }];
    },

    // ── Shared ───────────────────────────────────────────────────
    buildJson() {
      const c = this.config;
      return {
        BattlegroundWidth:  c.BattlegroundWidth,
        BattlegroundHeight: c.BattlegroundHeight,
        WoundsAttribute:    c.WoundsAttribute || 'W',
        AvailableDice:      c.AvailableDice,
        PivotDistanceMeasurement:    c.PivotDistanceMeasurement,
        Formations:                  this.selectedFormations.map((type, i) => ({ Index: i, Type: type })),
        DefaultFormation:            c.DefaultFormation,
        FormationChangingRules:      this.formationChangingRules
          .filter(r => r.Rule.trim())
          .map(r => ({ Rule: r.Rule.trim(), FormationType: r.FormationType })),
        MaxCloudSeparation:          c.MaxCloudSeparation,
        DefaultSoldierSelectionMode: c.DefaultSoldierSelectionMode,
        DefaultCardConfig: {
          Width:        c.Card_Width,
          Length:       c.Card_Length,
          IsRound:      c.Card_IsRound,
          ShowInitials: c.Card_ShowInitials,
          TextColor:    c.Card_TextColor,
          Color:        c.Card_Color,
          Border:       c.Card_Border,
          BorderColor:  c.Card_BorderColor,
        },
        ResultCalculatorAlgorithm: c.ResultCalculatorAlgorithm,
        ListParsingAlgorithm:      c.ListParsingAlgorithm,
        PointCounting: c.PointCounting,
        PointCountingStatusNames: this.pointCountingStatusNames
          .slice(0, this.pointCountingStatusCount)
          .map((name, i) => ({ Status: i, Name: name })),
        UseTurnController:  c.UseTurnController,
        DefaultZenithLight: c.DefaultZenithLight,
        ShownButtons: this.shownButtons.slice(),
      };
    },

    async addGame() {
      this.addGameSuccess = '';
      this.addGameError = '';

      const ownerRepo = parseOwnerRepo(this.githubRepo);
      if (!ownerRepo) {
        this.addGameError = 'A valid GitHub repository is required to build the data path.';
        return;
      }

      try {
        const authCheck = await fetch('/retrieveToken', {
          method: 'GET',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        const claim = await authCheck.json();
        if (!claim || !claim.user_id) {
          this.addGameError = 'Your session has expired. Please log in again.';
          return;
        }
      } catch {
        this.addGameError = 'Could not verify your session. Please log in again.';
        return;
      }

      this.addGameLoading = true;
      try {
        const safeName = this.gameName.trim().replace(/\s+/g, '_');
        const dataPath = `https://raw.githubusercontent.com/${ownerRepo}/refs/heads/main/${safeName}/manifest.json`;
        const res = await fetch('/AddNewGame/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            idGame: this.nextGameId,
            gameName: this.gameName.trim(),
            gameAvailability: 1,
            gameVersionLTS: 1,
            dataPath,
            tokens: this.tokensJson.filter(t => t.SkinId != null).map(t => ({ skinId: t.SkinId })),
            terrains: this.terrainsJson.filter(t => t.SkinId != null).map(t => ({ skinId: t.SkinId })),
          }),
        });
        const data = await res.json();
        if (data.success) {
          // Store the server-created ItemInstance IDs so tokens.json / terrain.json use them
          if (Array.isArray(data.createdInstances) && data.createdInstances.length > 0) {
            const map = {};
            for (const { skinId, instanceId } of data.createdInstances) {
              map[skinId] = instanceId;
            }
            this.createdSkinInstances = map;
          }
          // Assign the picked skin to the first unit (non-fatal if it fails)
          if (this.firstDataPickedSkin && this.firstData.firstUnitName) {
            try {
              const unitId = 'unit_' + this.firstData.firstUnitName.trim().toLowerCase().replace(/\s+/g, '_');
              await fetch('/AssignCatalogueSkin', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  skinId: this.firstDataPickedSkin.id,
                  profileId: unitId,
                  selectedOptions: [],
                  gameId: this.nextGameId,
                  costumeDefault: true,
                }),
              });
            } catch { /* non-fatal — game was already created */ }
          }
          this.addGameSuccess = 'Congratulations! Your game has been added to Warhall!';
          this.showSuccessModal = true;
          this.$nextTick(() => this.launchConfetti());
        } else {
          this.addGameError = 'Failed: ' + (data.error || 'Unknown error');
        }
      } catch (err) {
        this.addGameError = 'Request failed: ' + err.message;
      } finally {
        this.addGameLoading = false;
      }
    },

    // ── Success modal & confetti ──────────────────────────────
    closeSuccessModal() {
      if (this._confettiRafId) {
        cancelAnimationFrame(this._confettiRafId);
        this._confettiRafId = null;
      }
      if (this._confettiTimers) {
        this._confettiTimers.forEach(clearTimeout);
        this._confettiTimers = null;
      }
      this.showSuccessModal = false;
    },

    launchConfetti() {
      const canvas = this.$refs.confettiCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const W = canvas.width = canvas.offsetWidth || 600;
      const H = canvas.height = canvas.offsetHeight || 500;
      const colors = ['#f85149','#3fb950','#58a6ff','#ffa657','#d2a8ff','#ffd700','#ff69b4','#00e5ff'];

      const makeBurst = (cx, cy) =>
        Array.from({ length: 70 }, () => {
          const angle = Math.random() * Math.PI * 2;
          const speed = Math.random() * 9 + 2;
          return {
            x: cx, y: cy,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed - 4,
            r: Math.random() * 6 + 3,
            color: colors[Math.floor(Math.random() * colors.length)],
            opacity: 1,
            tilt: Math.random() * Math.PI * 2,
            tiltSpeed: (Math.random() - 0.5) * 0.25,
          };
        });

      let particles = [];
      const bursts = [
        { delay: 0,   cx: W * 0.5,  cy: H * 0.38 },
        { delay: 450, cx: W * 0.22, cy: H * 0.28 },
        { delay: 850, cx: W * 0.78, cy: H * 0.22 },
      ];
      this._confettiTimers = bursts.map(({ delay, cx, cy }) =>
        setTimeout(() => particles.push(...makeBurst(cx, cy)), delay)
      );

      const startTime = performance.now();
      const draw = (now) => {
        ctx.clearRect(0, 0, W, H);
        particles = particles.filter(p => p.opacity > 0.01);
        for (const p of particles) {
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.18;
          p.vx *= 0.99;
          p.tilt += p.tiltSpeed;
          p.opacity -= 0.007;
          ctx.save();
          ctx.globalAlpha = Math.max(0, p.opacity);
          ctx.fillStyle = p.color;
          ctx.translate(p.x, p.y);
          ctx.rotate(p.tilt);
          ctx.fillRect(-p.r / 2, -p.r / 4, p.r, p.r / 2);
          ctx.restore();
        }
        if (particles.length > 0 || now - startTime < 1000) {
          this._confettiRafId = requestAnimationFrame(draw);
        }
      };
      this._confettiRafId = requestAnimationFrame(draw);
    },

    // ── Color helpers ────────────────────────────────────────────
    hexToRgb(hex) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgb(${r}, ${g}, ${b})`;
    },
    rgbToHex(rgb) {
      if (rgb && rgb.startsWith('#')) return rgb;
      const m = rgb && rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (!m) return '#a3a3a3';
      return '#' + [m[1], m[2], m[3]].map(n => parseInt(n).toString(16).padStart(2, '0')).join('');
    },

    toggleFormation(name, checked) {
      const arr = this.selectedFormations.slice();
      if (checked && !arr.includes(name)) arr.push(name);
      else if (!checked) {
        const idx = arr.indexOf(name);
        if (idx !== -1) arr.splice(idx, 1);
      }
      this.selectedFormations = arr;
    },

    toggleChecklist(key, value, checked) {
      const arr = this.config[key].slice();
      if (checked && !arr.includes(value)) arr.push(value);
      else if (!checked) {
        const idx = arr.indexOf(value);
        if (idx !== -1) arr.splice(idx, 1);
      }
      this.$set(this.config, key, arr);
    },

    toggleShownButton(name, checked) {
      const arr = this.shownButtons.slice();
      if (checked && !arr.includes(name)) arr.push(name);
      else if (!checked) {
        const idx = arr.indexOf(name);
        if (idx !== -1) arr.splice(idx, 1);
      }
      this.shownButtons = arr;
    },

    // ── My First Data step ──────────────────────────────────────
    onFirstDataDone(data) {
      Object.assign(this.firstData, data);
      this.firstDataComplete = true;
      this.firstDataState = 'wizard-done';
      this.phase = 'config';
    },

    onFirstDataSkip() {
      this.openFinish();
    },

    onFirstDataAnswerNo() {
      this.firstDataState = 'no-answered';
    },

    onFirstDataBack() {
      this.firstDataState = 'question';
      this.phase = 'config';
    },

    onFirstDataWizardStart() {
      this.phase = 'firstdata';
    },

    onPickSkin() {
      this.showCataloguePicker = true;
    },

    onSkinPicked(skin) {
      this.firstDataPickedSkin = skin;
      this.showCataloguePicker = false;
    },

    addFormationRule() {
      const defaultType = this.selectedFormations[0] || '';
      this.formationChangingRules = [...this.formationChangingRules, { Rule: '', FormationType: defaultType }];
    },

    removeFormationRule(index) {
      const arr = this.formationChangingRules.slice();
      arr.splice(index, 1);
      this.formationChangingRules = arr;
    },

    downloadDeploymentsJson() {
      const json = JSON.stringify(this.buildDeploymentsJson(), null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'deployments.json';
      a.click();
      URL.revokeObjectURL(url);
    },

    _crc32(data) {
      const table = new Uint32Array(256);
      for (let i = 0; i < 256; i++) {
        let c = i;
        for (let j = 0; j < 8; j++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        table[i] = c;
      }
      let crc = 0xFFFFFFFF;
      for (let i = 0; i < data.length; i++) crc = table[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
      return (crc ^ 0xFFFFFFFF) >>> 0;
    },

    _buildZip(entries) {
      // entries: [{ path: string, content: string }]
      const enc = new TextEncoder();
      const parts = [];
      const centralDir = [];
      let offset = 0;

      for (const entry of entries) {
        const nameBytes = enc.encode(entry.path);
        const dataBytes = enc.encode(entry.content);
        const crc = this._crc32(dataBytes);

        // Local file header
        const lh = new Uint8Array(30 + nameBytes.length);
        const lv = new DataView(lh.buffer);
        lv.setUint32(0, 0x04034B50, true);
        lv.setUint16(4, 20, true);
        lv.setUint16(6, 0, true);
        lv.setUint16(8, 0, true); // compression: stored
        lv.setUint16(10, 0, true);
        lv.setUint16(12, 0, true);
        lv.setUint32(14, crc, true);
        lv.setUint32(18, dataBytes.length, true);
        lv.setUint32(22, dataBytes.length, true);
        lv.setUint16(26, nameBytes.length, true);
        lv.setUint16(28, 0, true);
        lh.set(nameBytes, 30);
        parts.push(lh, dataBytes);

        // Central directory entry
        const cd = new Uint8Array(46 + nameBytes.length);
        const cv = new DataView(cd.buffer);
        cv.setUint32(0, 0x02014B50, true);
        cv.setUint16(4, 20, true);
        cv.setUint16(6, 20, true);
        cv.setUint16(8, 0, true);
        cv.setUint16(10, 0, true);
        cv.setUint16(12, 0, true);
        cv.setUint16(14, 0, true);
        cv.setUint32(16, crc, true);
        cv.setUint32(20, dataBytes.length, true);
        cv.setUint32(24, dataBytes.length, true);
        cv.setUint16(28, nameBytes.length, true);
        cv.setUint16(30, 0, true);
        cv.setUint16(32, 0, true);
        cv.setUint16(34, 0, true);
        cv.setUint16(36, 0, true);
        cv.setUint32(38, 0, true);
        cv.setUint32(42, offset, true);
        cd.set(nameBytes, 46);
        centralDir.push(cd);

        offset += lh.length + dataBytes.length;
      }

      const cdSize = centralDir.reduce((s, c) => s + c.length, 0);
      const eocd = new Uint8Array(22);
      const ev = new DataView(eocd.buffer);
      ev.setUint32(0, 0x06054B50, true);
      ev.setUint16(4, 0, true);
      ev.setUint16(6, 0, true);
      ev.setUint16(8, entries.length, true);
      ev.setUint16(10, entries.length, true);
      ev.setUint32(12, cdSize, true);
      ev.setUint32(16, offset, true);
      ev.setUint16(20, 0, true);

      return new Blob([...parts, ...centralDir, eocd], { type: 'application/zip' });
    },

    async downloadAllAsZip() {
      const safeName = this.gameName.trim().replace(/[^a-z0-9_\-]/gi, '_');
      const factionFolder = this.firstData.firstUnitFaction
        ? this.firstData.firstUnitFaction.trim().replace(/\s+/g, '_')
        : 'Sample_Army';

      const entries = [];
      entries.push({ path: `${safeName}/gameconfig.json`,  content: JSON.stringify(this.buildJson(), null, 2) });
      entries.push({ path: `${safeName}/tokens.json`,      content: JSON.stringify(this.buildTokensJson(), null, 2) });
      entries.push({ path: `${safeName}/terrains.json`,    content: JSON.stringify(this.buildTerrainJson(), null, 2) });
      entries.push({ path: `${safeName}/objectives.json`,  content: JSON.stringify(this.buildObjectivesJson(), null, 2) });
      entries.push({ path: `${safeName}/deployments.json`, content: JSON.stringify(this.buildDeploymentsJson(), null, 2) });
      entries.push({ path: `${safeName}/rules.json`,       content: JSON.stringify(this.firstDataComplete ? this.buildGameRulesJson()  : [], null, 2) });
      entries.push({ path: `${safeName}/bases.json`,       content: JSON.stringify(this.firstDataComplete ? this.buildBasesJson()      : [], null, 2) });
      entries.push({ path: `${safeName}/hierarchy.json`,   content: JSON.stringify(this.buildHierarchyJson(), null, 2) });
      entries.push({ path: `${safeName}/equipment.json`,   content: '[]' });
      entries.push({ path: `${safeName}/options.json`,     content: '[]' });
      entries.push({ path: `${safeName}/cards.json`,       content: '[]' });
      entries.push({ path: `${safeName}/version.json`,     content: JSON.stringify({ version: 1, url: '' }) });
      entries.push({ path: `${safeName}/dummylist.json`,   content: '""' });

      if (this.firstDataComplete) {
        entries.push({ path: `${safeName}/${factionFolder}/units.json`,           content: JSON.stringify(this.buildFirstUnitsJson(), null, 2) });
        entries.push({ path: `${safeName}/${factionFolder}/profiles.json`,        content: JSON.stringify(this.buildFirstProfilesJson(), null, 2) });
        entries.push({ path: `${safeName}/${factionFolder}/rules.json`,           content: JSON.stringify(this.buildFactionRulesJson(), null, 2) });
        entries.push({ path: `${safeName}/${factionFolder}/statlines.json`,       content: JSON.stringify(this.buildFactionStatlinesJson(), null, 2) });
        entries.push({ path: `${safeName}/${factionFolder}/equipment.json`,       content: '[]' });
        entries.push({ path: `${safeName}/${factionFolder}/options.json`,         content: '[]' });
        entries.push({ path: `${safeName}/${factionFolder}/modifiedprofile.json`, content: '[]' });
        entries.push({ path: `${safeName}/${factionFolder}/modifiedunit.json`,    content: '[]' });
      }

      const manifestFiles = entries.map(e => e.path.replace(`${safeName}/`, ''));
      entries.push({ path: `${safeName}/manifest.json`, content: JSON.stringify({ version: 1, files: manifestFiles }, null, 2) });

      const zipBlob = this._buildZip(entries);
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    },

    resetState() {
      this.gameName = '';
      this.gameNameError = '';
      this.configStep = 0;
      this.config = buildDefaultConfig();
      this.githubRepo = '';
      this.githubToken = '';
      this.githubRepoError = '';
      this.githubTokenError = '';
      this.githubSuccess = '';
      this.githubError = '';
      this.addGameSuccess = '';
      this.addGameError = '';
      this.nextGameId = null;
      this.showSuccessModal = false;
      this.expandedSection = null;
      this.selectedFormations = [];
      this.formationSubStep = 0;
      this.formationError = '';
      this.formationChangingRules = [];
      this.shownButtons = DEFAULT_SHOWN_BUTTONS.slice();
      this.createdSkinInstances = {};
      this.pointCountingStatusNames = ['Alive', 'Dead', 'Fleeing', 'Decimated', 'Fleeing & Decimated'];
      this.firstData = { factions: [], sections: [], firstUnitName: '', firstUnitFaction: '', firstUnitSection: '', profileNames: [] };
      this.firstDataComplete = false;
      this.firstDataState = 'question';
      this.firstDataPickedSkin = null;
      this.showCataloguePicker = false;
      this.wizardMode = 'simple';
      this.tokensJson = [];
      this.tokenCatalogue = [];
      this.terrainsJson = [];
      this.deploymentsJson = [];
      this.objectivesJson = [];
    },

    startOver() {
      this.resetState();
      if (this.mode === 'edit') {
        this.$emit('reset');
        this.$router.push({ path: this.$route.path });
      } else {
        this.$router.push({ query: {} });
      }
    },
  },
};
</script>

<style scoped>
.creator-wrapper {
  min-height: 100vh;
  background: #0d1117;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
}

.help-fab {
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.4);
  transition: background 0.15s;
}
.help-fab:hover { background: #1d4ed8; }

.creator-card {
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  max-width: 860px;
  color: #e0e0e0;
  position: relative;
}

.creator-title {
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 1.2rem;
  color: #58a6ff;
}

.phase-subtitle {
  color: #9ab;
  margin-bottom: 1.4rem;
  font-size: 0.95rem;
}

.phase-name .field-row { max-width: 480px; }
.field-input-lg { font-size: 1.1rem; padding: 0.6rem 0.8rem; }

/* Named step stepper */
.step-stepper {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.8rem;
  overflow-x: auto;
  padding-bottom: 0.2rem;
}
.step-stepper-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 70px;
  position: relative;
}
.step-stepper-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 13px;
  left: calc(50% + 16px);
  right: calc(-50% + 16px);
  height: 2px;
  background: #30363d;
}
.step-stepper-item.step-done:not(:last-child)::after { background: #2563eb; }
.step-stepper-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #21262d;
  border: 2px solid #30363d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #8b949e;
  z-index: 1;
  flex-shrink: 0;
}
.step-stepper-item.step-active .step-stepper-dot { background: #2563eb; border-color: #2563eb; color: #fff; }
.step-stepper-item.step-done  .step-stepper-dot { background: #0d2a0d; border-color: #2ecc71; color: #2ecc71; }
.step-stepper-label {
  font-size: 0.68rem;
  color: #555;
  margin-top: 0.35rem;
  text-align: center;
  word-break: break-word;
  max-width: 88px;
  line-height: 1.3;
}
.step-stepper-item.step-active .step-stepper-label { color: #58a6ff; font-weight: 600; }
.step-stepper-item.step-done  .step-stepper-label { color: #2ecc71; }
.step-stepper-item.step-done { cursor: pointer; }
.step-stepper-item.step-done:hover .step-stepper-dot { border-color: #58a6ff; color: #58a6ff; }
.step-stepper-item.step-done:hover .step-stepper-label { color: #58a6ff; }

/* Formations */
.formation-wizard { min-height: 260px; }
.formation-substep-label {
  font-size: 0.78rem;
  color: #58a6ff;
  font-weight: 600;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.mb-sm { margin-bottom: 0.6rem; }

/* Formation changing rules */
.fcr-list { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
.fcr-row  { display: flex; align-items: center; gap: 0.5rem; }
.fcr-rule-input { flex: 1 1 180px; min-width: 0; }
.fcr-type-select { flex: 0 0 auto; min-width: 160px; }
.fcr-arrow { color: #8b949e; font-size: 1rem; flex-shrink: 0; }
.fcr-remove-btn {
  background: none;
  border: 1px solid #30363d;
  color: #8b949e;
  border-radius: 4px;
  cursor: pointer;
  padding: 0.25rem 0.55rem;
  font-size: 0.85rem;
  transition: border-color 0.15s, color 0.15s;
  flex-shrink: 0;
}
.fcr-remove-btn:hover { border-color: #f85149; color: #f85149; }
.fcr-add-btn { margin-top: 0.5rem; }

/* Fields grid */
.step-content { min-height: 320px; }
.fields-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.2rem; }
.field-row { display: flex; flex-direction: column; gap: 0.3rem; }
.field-row.full-width { grid-column: 1 / -1; }
.step-intro { grid-column: 1 / -1; margin-bottom: 0.25rem; }
.field-label { font-weight: 600; font-size: 0.9rem; color: #c0c0d0; }
.field-desc  { font-size: 0.78rem; color: #7a8aaa; line-height: 1.4; margin: 0; }
.field-input {
  background: #21262d;
  border: 1px solid #30363d;
  color: #e0e0e0;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
}
.field-input:focus { border-color: #58a6ff; }
.field-input-narrow { max-width: 140px; }
.field-error { color: #f85149; font-size: 0.78rem; margin: 0; }
.required { color: #f85149; }

/* Toggle */
.toggle-label { display: flex; align-items: center; gap: 0.6rem; cursor: pointer; user-select: none; margin-top: 0.2rem; }
.toggle { width: 44px; height: 24px; background: #30363d; border-radius: 12px; position: relative; transition: background 0.2s; flex-shrink: 0; }
.toggle-on { background: #2563eb; }
.toggle-thumb { width: 18px; height: 18px; background: #fff; border-radius: 50%; position: absolute; top: 3px; left: 3px; transition: transform 0.2s; }
.toggle-on .toggle-thumb { transform: translateX(20px); }
.toggle-text { font-size: 0.85rem; color: #aaa; min-width: 28px; }

/* Navigation */
.nav-buttons { display: flex; justify-content: space-between; align-items: center; margin-top: 2rem; padding-top: 1rem; border-top: 1px solid #30363d; gap: 0.5rem; }

/* Finish phase – accordion */
.accordion-section { border: 1px solid #30363d; border-radius: 8px; margin-bottom: 0.75rem; overflow: hidden; }
.finish-section { border: 1px solid #30363d; border-radius: 8px; padding: 1rem 1.25rem; margin-bottom: 0.75rem; background: #161b22; }
.finish-section-title { display: flex; align-items: center; gap: 0.5rem; font-size: 1rem; font-weight: 600; color: #e6edf3; margin: 0 0 0.75rem; }
.finish-section--publish { display: flex; flex-direction: column; align-items: flex-start; }
.accordion-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1rem;
  background: #161b22;
  border: none;
  color: #c0c0d0;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}
.accordion-header:hover { background: #1c2128; }
.accordion-arrow { font-size: 0.7rem; color: #58a6ff; }
.accordion-body { padding: 1rem; border-top: 1px solid #30363d; background: #0d1117; }

/* Repo source toggle */
.repo-source-toggle { display: flex; border: 1px solid #30363d; border-radius: 6px; overflow: hidden; width: fit-content; }
.repo-source-btn { padding: 0.45rem 0.9rem; font-size: 0.85rem; font-weight: 600; border: none; background: #161b22; color: #8b949e; cursor: pointer; transition: background 0.15s, color 0.15s; }
.repo-source-btn.active { background: #2563eb; color: #fff; }
.repo-source-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* Buttons step */
.buttons-step { width: 100%; }
.btn-group { margin-bottom: 1.4rem; }
.btn-group-label { font-size: 0.8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #8b949e; margin: 0 0 0.5rem 0; padding-bottom: 0.35rem; border-bottom: 1px solid #21262d; }
.buttons-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 0.6rem; margin-top: 0.8rem; }
.btn-option { display: flex; align-items: center; gap: 0.75rem; padding: 0.65rem 0.85rem; border: 1px solid #30363d; border-radius: 8px; cursor: pointer; background: #161b22; transition: border-color 0.15s, background 0.15s; }
.btn-option:hover { border-color: #58a6ff; }
.btn-option--checked { border-color: #58a6ff; background: #1c2942; }
.btn-option-check { flex-shrink: 0; width: 15px; height: 15px; accent-color: #58a6ff; cursor: pointer; }
.btn-option-icon { flex-shrink: 0; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; }
.btn-option-icon img { width: 28px; height: 28px; object-fit: contain; }
.btn-option-icon-placeholder { width: 28px; height: 28px; border-radius: 6px; background: #30363d; }
.btn-option-text { display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
.btn-option-name { font-size: 0.88rem; color: #e6edf3; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.btn-option-desc { font-size: 0.75rem; color: #8b949e; line-height: 1.3; }

/* Status names */
.status-names-block { margin-top: 1.4rem; }
.status-name-row { display: flex; align-items: center; gap: 0.6rem; margin-top: 0.5rem; }
.status-name-index { min-width: 1.4rem; font-size: 0.8rem; color: #8b949e; text-align: right; }

/* Color picker */
.colorpicker-row { display: flex; align-items: center; gap: 0.6rem; margin-top: 0.2rem; }
.colorpicker-swatch { width: 40px; height: 32px; padding: 2px; border: 1px solid #30363d; border-radius: 6px; background: #21262d; cursor: pointer; flex-shrink: 0; }

/* Checklist */
.checklist-group { display: grid; grid-template-columns: repeat(4, max-content); gap: 0.5rem 1.6rem; margin-top: 0.2rem; }
.checklist-item { display: flex; align-items: center; gap: 0.4rem; font-size: 0.88rem; color: #c0c0d0; cursor: pointer; user-select: none; }
.checklist-item input[type="checkbox"] { accent-color: #2563eb; width: 15px; height: 15px; cursor: pointer; }
.checklist-icon { width: 24px; height: 24px; object-fit: contain; }

/* GitHub icon */
.gh-icon { width: 18px; height: 18px; vertical-align: middle; color: #c0c0d0; }

.mt-sm { margin-top: 0.75rem; }

/* PAT help */
.label-with-help { display: flex; align-items: center; gap: 0.5rem; }
.help-btn { width: 18px; height: 18px; border-radius: 50%; border: 1px solid #58a6ff; background: transparent; color: #58a6ff; font-size: 0.7rem; font-weight: 700; line-height: 1; cursor: pointer; padding: 0; flex-shrink: 0; transition: background 0.15s, color 0.15s; }
.help-btn:hover { background: #58a6ff; color: #0d1117; }
.help-box { background: #21262d; border: 1px solid #30363d; border-radius: 6px; padding: 0.75rem 1rem; font-size: 0.8rem; color: #cdd9e5; line-height: 1.6; margin-bottom: 0.4rem; }
.help-box ol { margin: 0; padding-left: 1.2rem; }
.help-box li { margin-bottom: 0.3rem; }
.help-box code { background: #161b22; border-radius: 3px; padding: 0 4px; font-size: 0.78rem; }

/* Buttons */
.btn { padding: 0.5rem 1.2rem; border-radius: 6px; border: none; font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: opacity 0.2s, transform 0.1s; display: inline-block; }
.btn:active { transform: scale(0.97); }
.btn:disabled { opacity: 0.45; cursor: not-allowed; }
.btn-primary { background: #2563eb; color: #fff; }
.btn-primary:hover:not(:disabled) { opacity: 0.85; }
.btn-secondary { background: #21262d; color: #cdd9e5; border: 1px solid #30363d; }
.btn-secondary:hover { opacity: 0.8; }
.btn-done { background: #2ecc71; color: #111; }
.btn-done:hover:not(:disabled) { opacity: 0.85; }
.btn-add-game { width: 100%; padding: 0.85rem; font-size: 1.05rem; font-weight: 700; background: #2ecc71; color: #111; border: none; border-radius: 8px; cursor: pointer; transition: background 0.15s; }
.btn-add-game:hover:not(:disabled) { background: #27ae60; }
.btn-add-game:disabled { opacity: 0.5; cursor: not-allowed; }

/* Messages */
.message { margin-top: 0.75rem; padding: 0.65rem 1rem; border-radius: 6px; font-size: 0.88rem; word-break: break-all; }
.message-success { background: #1a2e1a; border: 1px solid #2ecc71; color: #7fffaa; }
.message-error   { background: #2a1a1a; border: 1px solid #f85149; color: #ffb3ae; }

/* Success modal */
.success-modal-overlay { position: fixed; inset: 0; z-index: 9999; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.78); }
.confetti-canvas { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
.success-modal { position: relative; background: #161b22; border: 2px solid #3fb950; border-radius: 16px; padding: 2.5rem 3rem; text-align: center; max-width: 460px; width: 90%; box-shadow: 0 8px 48px rgba(63,185,80,0.3); z-index: 1; }
.success-modal-icon { font-size: 3.5rem; line-height: 1; margin-bottom: 1rem; }
.success-modal-title { font-size: 1.75rem; font-weight: 800; color: #e6edf3; margin: 0 0 0.5rem 0; }
.success-modal-text { font-size: 1.1rem; color: #8b949e; margin: 0 0 1.75rem 0; }
.success-modal-close { font-size: 1rem; padding: 0.75rem 2.5rem; }
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.22s; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }

/* Upload progress */
.upload-progress-wrap { position: relative; height: 8px; background: #21262d; border-radius: 4px; overflow: hidden; max-width: 420px; }
.upload-progress-bar  { height: 100%; background: #2563eb; border-radius: 4px; transition: width 0.25s ease; }
.upload-progress-label { display: block; font-size: 0.78rem; color: #8b949e; margin-top: 0.3rem; }
.upload-folder-link { display: inline-block; margin-top: 0.35rem; color: #58a6ff; text-decoration: none; font-size: 0.9rem; }
.upload-folder-link:hover { text-decoration: underline; }

/* Tokens step */
.tokens-step { margin-top: 0.5rem; }
.tokens-step .skin-grid { max-height: 420px; overflow-y: auto; }
.terrain-details-list { margin-top: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
.terrain-detail-row { background: #161b22; border: 1px solid #30363d; border-radius: 6px; padding: 0.75rem 1rem; }
.terrain-detail-fields { display: flex; flex-direction: row; align-items: flex-end; gap: 0.75rem; }
.terrain-detail-thumb { width: 56px; height: 56px; object-fit: contain; border-radius: 4px; background: #0d1117; border: 1px solid #30363d; flex-shrink: 0; align-self: center; }
.terrain-detail-field { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.85rem; color: #8b949e; }
.terrain-detail-field--name { flex: 1; min-width: 120px; }
.terrain-detail-field--narrow { width: 90px; flex-shrink: 0; }
.terrain-detail-field input[type="text"],
.terrain-detail-field input[type="number"] { width: 100%; }
.terrain-shape-toggle { display: flex; border: 1px solid #30363d; border-radius: 6px; overflow: hidden; margin-top: 0.1rem; }
.terrain-shape-btn { flex: 1; padding: 0.3rem 0.75rem; background: #0d1117; color: #8b949e; border: none; cursor: pointer; font-size: 0.82rem; transition: background 0.15s, color 0.15s; }
.terrain-shape-btn + .terrain-shape-btn { border-left: 1px solid #30363d; }
.terrain-shape-btn.active { background: #1f6feb; color: #fff; font-weight: 600; }

.detail-remove-btn { width: 28px; height: 28px; background: #da3633; border: none; border-radius: 4px; color: #fff; font-size: 1rem; cursor: pointer; flex-shrink: 0; display: flex; align-items: center; justify-content: center; align-self: flex-end; transition: background 0.15s; padding: 0; line-height: 1; }
.detail-remove-btn:hover { background: #f85149; }

/* Objectives step */
.objectives-step { margin-top: 0.5rem; }
.obj-list { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
.obj-item { background: #21262d; border: 1px solid #30363d; border-radius: 6px; padding: 0.65rem 0.85rem; }
.obj-item-header { display: flex; align-items: center; gap: 0.75rem; }
.obj-item-name { font-weight: 600; font-size: 0.9rem; color: #e6edf3; flex: 1; }
.obj-item-tokens { font-size: 0.78rem; color: #8b949e; }
.obj-item--clickable { cursor: pointer; }
.obj-item--clickable:hover { background: rgba(255,255,255,0.07); border-radius: 6px; }
.obj-remove-btn { background: none; border: 1px solid #30363d; color: #8b949e; border-radius: 4px; cursor: pointer; padding: 0.2rem 0.5rem; font-size: 0.8rem; transition: border-color 0.15s, color 0.15s; flex-shrink: 0; }
.obj-remove-btn:hover { border-color: #f85149; color: #f85149; }
.new-objective-form { background: #161b22; border: 1px solid #30363d; border-radius: 8px; padding: 1rem 1.2rem; margin-top: 0.75rem; }
.obj-token-palette { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.4rem; margin-bottom: 0.75rem; }
.obj-token-thumb { width: 64px; height: 64px; object-fit: contain; border: 1px solid #30363d; border-radius: 6px; background: #21262d; cursor: grab; padding: 2px; transition: border-color 0.15s; }
.obj-token-thumb:hover { border-color: #58a6ff; }
.obj-token-thumb:active { cursor: grabbing; }
.obj-battlefield-wrap { margin-top: 0.75rem; display: flex; flex-direction: column; align-items: center; }
.obj-battlefield {
  position: relative;
  width: 100%;
  max-width: 600px;
  background: rgb(17, 30, 36);
  border: 3px solid #ffffff;
  border-radius: 4px;
  overflow: hidden;
  cursor: crosshair;
}
.obj-bf-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  color: #3a5a3a;
  pointer-events: none;
  user-select: none;
}
.obj-coord-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.78);
  color: #fff;
  font-size: 0.75rem;
  padding: 3px 8px;
  border-radius: 4px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 10;
}
.obj-placed-token {
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  z-index: 5;
  user-select: none;
}
.obj-placed-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  display: block;
  border-radius: 4px;
  border: 2px solid transparent;
  transition: border-color 0.15s;
  pointer-events: none;
}
.obj-placed-token:hover .obj-placed-img { border-color: #58a6ff; }
.obj-placed-token--selected .obj-placed-img { border-color: #58a6ff; box-shadow: 0 0 0 2px rgba(88,166,255,0.4); }
.obj-placed-coords {
  font-size: 0.6rem;
  color: #fff;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 3px;
  padding: 1px 4px;
  white-space: nowrap;
  margin-top: 2px;
  pointer-events: none;
  line-height: 1.4;
}
.obj-selected-panel {
  margin-top: 0.75rem;
  background: #1c2942;
  border: 1px solid #58a6ff;
  border-radius: 6px;
  padding: 0.55rem 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.obj-selected-name { font-size: 0.88rem; color: #e6edf3; font-weight: 500; flex: 1; min-width: 80px; }
.obj-selected-fields { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.obj-remove-token-btn {
  width: 32px;
  height: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #c0392b;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
  padding: 0;
}
.obj-remove-token-btn:hover { background: #e74c3c; }

/* ── Deployments step ── */
.deployments-step { display: flex; flex-direction: column; }
.dep-list { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 0.5rem; }
.dep-item { border: 1px solid #30363d; border-radius: 6px; padding: 0.5rem 0.75rem; background: #161b22; }
.dep-item--clickable { cursor: pointer; }
.dep-item--clickable:hover { background: rgba(255,255,255,0.07); }
.dep-item-header { display: flex; align-items: center; gap: 0.6rem; }
.dep-item-thumb { width: 48px; height: 32px; object-fit: cover; border-radius: 4px; border: 1px solid #30363d; flex-shrink: 0; }
.dep-item-name { font-weight: 600; font-size: 0.9rem; color: #e6edf3; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dep-remove-btn { background: none; border: 1px solid #30363d; color: #8b949e; border-radius: 4px; cursor: pointer; padding: 0.2rem 0.5rem; font-size: 0.8rem; transition: border-color 0.15s, color 0.15s; flex-shrink: 0; }
.dep-remove-btn:hover { border-color: #f85149; color: #f85149; }
.new-deployment-form { background: #161b22; border: 1px solid #30363d; border-radius: 8px; padding: 1rem 1.2rem; margin-top: 0.75rem; display: flex; flex-direction: column; align-items: center; }
.new-deployment-form .field-row, .new-deployment-form > p.field-label { align-self: flex-start; }
.dep-xyz-row { display: flex; gap: 1rem; flex-wrap: wrap; align-items: flex-end; justify-content: center; }
.dep-xyz-field { display: flex; flex-direction: column; gap: 0.3rem; }
.dep-xyz-panel { margin-top: 1rem; padding: 0.75rem 1.2rem; background: #1c2a1e; border: 1px solid #3fb950; border-radius: 8px; box-shadow: 0 0 0 2px #3fb95033; width: 100%; box-sizing: border-box; }
.dep-xyz-panel-label { font-size: 0.8rem; font-weight: 600; color: #3fb950; margin: 0 0 0.5rem 0; text-align: center; letter-spacing: 0.04em; text-transform: uppercase; }
.dep-image-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.6rem; margin-top: 0.5rem; }
.dep-image-option { position: relative; border: 2px solid #30363d; border-radius: 6px; cursor: pointer; overflow: hidden; transition: border-color 0.15s; }
.dep-image-option:hover { border-color: #58a6ff; }
.dep-image-option--selected { border-color: #3fb950; box-shadow: 0 0 0 2px #3fb95066; }
.dep-image-count { position: absolute; top: 4px; right: 4px; background: #3fb950; color: #0d1117; font-size: 0.72rem; font-weight: 700; border-radius: 10px; padding: 1px 6px; pointer-events: none; line-height: 1.4; }
.dep-image-thumb { width: 100%; aspect-ratio: 4/3; object-fit: cover; display: block; }

/* Catalogue skin picker overlay */
.catalogue-picker-overlay { position: fixed; inset: 0; z-index: 8888; background: #0d1117; display: flex; flex-direction: column; overflow: hidden; }
.catalogue-picker-header { display: flex; align-items: center; gap: 1rem; padding: 0.75rem 1.5rem; background: #161b22; border-bottom: 1px solid #30363d; flex-shrink: 0; }
.catalogue-picker-title { color: #94a3b8; font-size: 0.9rem; }
.catalogue-picker-title strong { color: #e6edf3; }
.catalogue-picker-back { flex-shrink: 0; }
.catalogue-picker-body { flex: 1; overflow-y: auto; }
</style>
